import { Component, Injector, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { EnumerationItem } from 'src/app/models/simple-enum-item.model';
import { ApplicationContextBoundComponent } from 'src/app/shared/components/application-context-bound.component';
import {
  CdBuydownContributorType,
  LoanInformationSection
} from 'src/app/cd-request/models/closing-disclosure-doc-request.model';
import { BuydownTypeEnum } from 'src/app/models/mortgage.model';
import { EnumerationService } from 'src/app/services/enumeration-service';

export interface LoanInformationFormData {
  loanNumber: string;
  programName: string;
  buydown: boolean | null;
  buydownType: BuydownTypeEnum | null;
  buydownContributor: CdBuydownContributorType | null;
  changeBuydown: boolean;
  loanPurpose: string;
  amortizationType: string;
  appraisedValue: number | null;
  noteRate: number | null;
  baseLoanAmount: number | null;
  totalLoanAmount: number | null;
  impoundsWaived: boolean | null;
  salesPrice: number | null;
  closeOfEscrowDate: string;
  firstPaymentDate: string;
  ltv: number | null;
  cltv: number | null;
}

@Component({
  selector: 'loan-information',
  templateUrl: './loan-information.component.html',
  styleUrls: ['./loan-information.component.scss'],
  standalone: false
})
export class LoanInformationComponent extends ApplicationContextBoundComponent implements OnInit, OnChanges {

  @ViewChild('loanInfoForm') loanInfoForm: NgForm;

  @Input() loanInformation: LoanInformationSection | undefined;

  formData: LoanInformationFormData = {
    loanNumber: '',
    programName: '',
    buydown: null,
    buydownType: null,
    buydownContributor: null,
    changeBuydown: false,
    loanPurpose: '',
    amortizationType: '',
    appraisedValue: null,
    noteRate: null,
    baseLoanAmount: null,
    totalLoanAmount: null,
    impoundsWaived: null,
    salesPrice: null,
    closeOfEscrowDate: '',
    firstPaymentDate: '',
    ltv: null,
    cltv: null
  };

  isLoading: boolean = false;

  yesNoOptions: EnumerationItem[] = [];

  buydownTypeOptions: EnumerationItem[] = [
    { value: BuydownTypeEnum.None, name: 'None' },
    { value: BuydownTypeEnum.OneOne, name: '1-1' },
    { value: BuydownTypeEnum.OneZero, name: '1-0' },
    { value: BuydownTypeEnum.ThreeTwoOne, name: '3-2-1' },
    { value: BuydownTypeEnum.TwoOne, name: '2-1' },
    { value: BuydownTypeEnum.OneOneOne, name: '1-1-1' }
  ];

  buydownContributorOptions: EnumerationItem[] = [
    { value: CdBuydownContributorType.Borrower, name: 'Borrower' },
    { value: CdBuydownContributorType.Builder, name: 'Builder' },
    { value: CdBuydownContributorType.Employer, name: 'Employer' },
    { value: CdBuydownContributorType.Lender, name: 'Lender' },
    { value: CdBuydownContributorType.NonParentRelative, name: 'Non-Parent Relative' },
    { value: CdBuydownContributorType.Parent, name: 'Parent' },
    { value: CdBuydownContributorType.Seller, name: 'Seller' },
    { value: CdBuydownContributorType.Unassigned, name: 'Unassigned' },
    { value: CdBuydownContributorType.UnrelatedFriend, name: 'Unrelated Friend' }
  ];

  constructor(
    protected readonly injector: Injector,
    private readonly _enumsService: EnumerationService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.yesNoOptions = this._enumsService.getYesNoEnumItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['loanInformation']) {
      this.populateFormFromApiData();
    }
  }

  validateForm(): boolean {
    if (this.loanInfoForm) {
      this.loanInfoForm.form.markAllAsTouched();
      return this.loanInfoForm.form.valid;
    }
    return true;
  }

  getFormData(): LoanInformationFormData {
    return { ...this.formData };
  }

  private populateFormFromApiData(): void {
    if (!this.loanInformation) {
      return;
    }

    this.formData.loanNumber = this.loanInformation.loanNumber || '';
    this.formData.programName = this.loanInformation.programName || '';
    this.formData.buydown = this.loanInformation.buydown ?? null;
    this.formData.buydownType = this.loanInformation.buydownType ?? null;
    this.formData.buydownContributor = this.loanInformation.buydownContributor ?? null;
    this.formData.changeBuydown = this.loanInformation.changeBuydown ?? false;
    this.formData.loanPurpose = this.loanInformation.loanPurpose || '';
    this.formData.amortizationType = this.loanInformation.amortizationType || '';
    this.formData.appraisedValue = this.loanInformation.appraisedValue ?? null;
    this.formData.noteRate = this.loanInformation.noteRate ?? null;
    this.formData.baseLoanAmount = this.loanInformation.baseLoanAmount ?? null;
    this.formData.totalLoanAmount = this.loanInformation.totalLoanAmount ?? null;
    this.formData.impoundsWaived = this.loanInformation.impoundsWaived ?? null;
    this.formData.salesPrice = this.loanInformation.salesPrice ?? null;
    this.formData.closeOfEscrowDate = this.toDateInputValue(this.loanInformation.closeOfEscrowDate);
    this.formData.firstPaymentDate = this.toDateInputValue(this.loanInformation.firstPaymentDate);
    this.formData.ltv = this.loanInformation.ltv ?? null;
    this.formData.cltv = this.loanInformation.cltv ?? null;
  }

  private toDateInputValue(value: string | undefined): string {
    if (!value) {
      return '';
    }

    const isoDate = /^(\d{4})-(\d{2})-(\d{2})(?:T|$)/.exec(value);
    return isoDate ? `${isoDate[2]}/${isoDate[3]}/${isoDate[1]}` : value;
  }

  formatCurrency(value: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }

  formatPercentage(value: number | null): string {
    if (value === null || value === undefined) {
      return '';
    }
    return `${value.toFixed(3)}%`;
  }

  formatLtvCltv(): string {
    const ltv = this.formData.ltv;
    const cltv = this.formData.cltv;

    if (ltv === null && cltv === null) {
      return '';
    }

    const ltvStr = ltv !== null ? `${ltv.toFixed(2)}%` : 'N/A';
    const cltvStr = cltv !== null ? `${cltv.toFixed(2)}%` : 'N/A';

    return `${ltvStr} / ${cltvStr}`;
  }
}
